<template>
    <div class="cpf-container" :style="containerStyle">
        <a-card class="cpf-card">
            <template #title>
                <a-breadcrumb separator=">">
                    <a-breadcrumb-item>CPF</a-breadcrumb-item>
                </a-breadcrumb>
            </template>

            <a-alert
                :message="`Olá, ${displayName}!`"
                description="Para continuar, você precisa preencher seu CPF abaixo."
                type="info"
                show-icon
                style="margin-bottom: 16px"
            />

            <a-form
                :model="form"
                :rules="rules"
                ref="formRef"
                layout="vertical"
                @finish="handleSave"
            >
                <a-form-item label="CPF" name="cpf">
                    <a-input
                        v-model:value="form.cpf"
                        v-mask="'###.###.###-##'"
                        :maxlength="14"
                        inputmode="numeric"
                        autocomplete="off"
                        placeholder="000.000.000-00"
                    />
                </a-form-item>

                <a-form-item>
                    <a-button
                        type="primary"
                        html-type="submit"
                        :loading="loading"
                        :disabled="!isCpfValid"
                        block
                    >
                        Salvar
                    </a-button>
                </a-form-item>
            </a-form>
        </a-card>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { notification } from 'ant-design-vue'

const props = defineProps({
    user: { type: Object, required: true }
})

const displayName = computed(() =>
    props.user?.name ?? props.user?.full_name ?? props.user?.username ?? 'usuário'
)

const formRef = ref(null)
const loading = ref(false)
const loginBackground = ref('/img/background-login.jpg')

const containerStyle = computed(() => ({
    backgroundImage: `url('${loginBackground.value}')`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    backgroundSize: 'cover',
}))

async function loadLoginBackground() {
    try {
        const { data } = await window.axios.get('/api/configurations/personalizations/public')
        if (data?.data?.login_background) {
            loginBackground.value = data.data.login_background
        }
    } catch (_) {
        // Mantém imagem padrão.
    }
}

onMounted(loadLoginBackground)
const form = ref({
    cpf: props.user?.cpf ? formatCpf(String(props.user.cpf)) : ''
})

// --- Utils ---
function onlyDigits (v = '') { return (v || '').replace(/\D/g, '') }
function formatCpf (v = '') {
    const d = onlyDigits(v).slice(0, 11)
    return d
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1-$2')
}
function isValidCPF (value = '') {
    const cpf = onlyDigits(value)
    if (!cpf || cpf.length !== 11) return false
    if (/^(\d)\1{10}$/.test(cpf)) return false
    let sum = 0
    for (let i = 0; i < 9; i++) sum += parseInt(cpf[i]) * (10 - i)
    let rev = 11 - (sum % 11)
    if (rev >= 10) rev = 0
    if (rev !== parseInt(cpf[9])) return false
    sum = 0
    for (let i = 0; i < 10; i++) sum += parseInt(cpf[i]) * (11 - i)
    rev = 11 - (sum % 11)
    if (rev >= 10) rev = 0
    return rev === parseInt(cpf[10])
}
const isCpfValid = computed(() => isValidCPF(form.value.cpf))

const rules = {
    cpf: [
        { required: true, message: 'O CPF é obrigatório.', trigger: 'blur' },
        {
            validator: (_, value) =>
                isValidCPF(value) ? Promise.resolve() : Promise.reject(new Error('CPF inválido.')),
            trigger: 'blur'
        }
    ]
}

const handleSave = async () => {
    try {
        await formRef.value.validate()
        loading.value = true
        const cpfDigits = onlyDigits(form.value.cpf)

        await window.axios.post('api/configurations/users/insert-cpf', {cpf: cpfDigits})
        notification.success({ message: 'CPF salvo com sucesso!' })
        window.location.reload()
    } catch (err) {
        notification.error({
            message: 'Não foi possível salvar o CPF',
            description: 'Verifique o CPF informado.'
        })
    } finally {
        loading.value = false
    }
}
</script>

<style scoped>
.cpf-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
}
.cpf-card {
    width: 400px;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
}
</style>
